"use client";

import { apiFetch } from "@/src/lib/api-fetch";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "@/src/lib/next-compat";
import { ArrowLeft } from "lucide-react";
import { LessonJsonBuilder } from "@/components/admin/canonical-builder/LessonJsonBuilder";
import type { OrganizationOption, PromptDraft } from "@/components/admin/canonical-builder/types";
import { getJsonError } from "@/components/admin/canonical-builder/types";
import { useCanonicalHierarchyBuilder } from "@/components/admin/canonical-builder/useCanonicalHierarchyBuilder";
import { useCanonicalDraftHydration } from "@/components/admin/canonical-builder/useCanonicalDraftHydration";
import { createClientId } from "@/components/admin/canonical-builder/clientId";
import { SaveProgressOverlay } from "@/components/admin/canonical-builder/SaveProgressOverlay";
import {
    CourseBasicsSection,
    MetadataSection,
    PromptsSection,
    LevelsEditor,
    PreviewPanel,
} from "@/components/admin/canonical-builder/BuilderSections";

type SaveOperationGroups = {
    created: Record<string, number>;
    updated: Record<string, number>;
    deleted: Record<string, number>;
};

const formatOperationCounts = (ops?: Record<string, number>) =>
    Object.entries(ops || {})
        .filter(([, count]) => count > 0)
        .map(([key, count]) => `${key}: ${count}`)
        .join(", ");

const formatOperationSummary = (operations?: SaveOperationGroups) => {
    const groups = [
        { label: "created", value: formatOperationCounts(operations?.created) },
        { label: "updated", value: formatOperationCounts(operations?.updated) },
        { label: "deleted", value: formatOperationCounts(operations?.deleted) },
    ];
    return groups
        .filter((group) => Boolean(group.value))
        .map((group) => `${group.label}(${group.value})`)
        .join(" • ");
};

export default function CanonicalLessonsBuilderPage() {
    const navigate = useNavigate();
    const searchParams = useSearchParams();
    const parsedCourseId = Number(searchParams.get("courseId"));
    const isEditMode = Number.isInteger(parsedCourseId) && parsedCourseId > 0;
    const editingCourseId = isEditMode ? parsedCourseId : null;
    const [courseTitle, setCourseTitle] = useState("");
    const [description, setDescription] = useState("");
    const [domains, setDomains] = useState<string[]>([""]);
    const [futureGoals, setFutureGoals] = useState<string[]>([""]);
    const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
    const [prompts, setPrompts] = useState<PromptDraft>({
        generation_prompt: "",
        proactive_prompt: "",
        quiz_prompt: "",
        placement_quiz_prompt: "",
        remedial_lesson_prompt: "",
    });
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isHydrating, setIsHydrating] = useState(false);
    const [activeTab, setActiveTab] = useState<"details" | "metadata" | "prompts" | "structure" | "create">("details");
    const [saveStatusText, setSaveStatusText] = useState<string | null>(null);
    const [saveProgressLog, setSaveProgressLog] = useState<Array<{ stage: string; message: string }>>([]);
    const [showPostSaveReview, setShowPostSaveReview] = useState(false);
    const postSaveDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const dismissSaveReview = useCallback(() => {
        if (postSaveDismissTimerRef.current) {
            clearTimeout(postSaveDismissTimerRef.current);
            postSaveDismissTimerRef.current = null;
        }
        setShowPostSaveReview(false);
        setSaveProgressLog([]);
        setSaveStatusText(null);
    }, []);

    useEffect(() => {
        if (!showPostSaveReview) return;
        postSaveDismissTimerRef.current = setTimeout(() => {
            dismissSaveReview();
        }, 2000);
        return () => {
            if (postSaveDismissTimerRef.current) {
                clearTimeout(postSaveDismissTimerRef.current);
                postSaveDismissTimerRef.current = null;
            }
        };
    }, [showPostSaveReview, dismissSaveReview]);
    const {
        levels,
        replaceLevels,
        addLevel,
        updateLevelName,
        removeLevel,
        addModule,
        updateModuleTitle,
        removeModule,
        addLesson,
        updateLessonField,
        removeLesson,
    } = useCanonicalHierarchyBuilder();
    const {
        isHydrating: isHydratingDraft,
        hydrationError,
        hydrationData,
    } = useCanonicalDraftHydration({ editingCourseId });

    const totalModules = useMemo(
        () => levels.reduce((acc, level) => acc + level.modules.length, 0),
        [levels],
    );
    const totalLessons = useMemo(
        () =>
            levels.reduce(
                (acc, level) =>
                    acc + level.modules.reduce((moduleAcc, module) => moduleAcc + module.lessons.length, 0),
                0,
            ),
        [levels],
    );

    const hasInvalidLessonJson = useMemo(
        () =>
            levels.some((level) =>
                level.modules.some((module) =>
                    module.lessons.some((lesson) => lesson.content.trim().length > 0 && getJsonError(lesson.content)),
                ),
            ),
        [levels],
    );
    const hasEmptyLessonContent = useMemo(
        () =>
            levels.some((level) =>
                level.modules.some((module) => module.lessons.some((lesson) => lesson.content.trim().length === 0)),
            ),
        [levels],
    );

    const normalizedDomains = useMemo(() => domains.map((d) => d.trim()).filter(Boolean), [domains]);
    const normalizedFutureGoals = useMemo(
        () => futureGoals.map((g) => g.trim()).filter(Boolean),
        [futureGoals],
    );

    const isSaveDisabled = useMemo(() => {
        if (isHydrating) return true;
        if (!courseTitle.trim()) return true;
        if (!description.trim()) return true;
        if (!selectedOrganizationId) return true;
        if (normalizedDomains.length === 0 || normalizedFutureGoals.length === 0) return true;
        if (!prompts.generation_prompt.trim()) return true;
        if (!prompts.proactive_prompt.trim()) return true;
        if (!prompts.quiz_prompt.trim()) return true;
        if (!prompts.placement_quiz_prompt.trim()) return true;
        if (!prompts.remedial_lesson_prompt.trim()) return true;
        if (totalLessons === 0) return true;
        if (hasEmptyLessonContent) return true;
        if (hasInvalidLessonJson) return true;
        return false;
    }, [
        courseTitle,
        description,
        selectedOrganizationId,
        normalizedDomains.length,
        normalizedFutureGoals.length,
        prompts,
        totalLessons,
        hasEmptyLessonContent,
        hasInvalidLessonJson,
        isHydrating,
    ]);


    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await apiFetch("admin/organizations");
                if (!res.ok) return;
                const data = (await res.json()) as OrganizationOption[];
                if (!mounted) return;
                setOrganizations(data);
                if (data?.[0]?.id) {
                    setSelectedOrganizationId(String(data[0].id));
                }
            } catch {
                // Best-effort only; save validation will still require org selection.
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        setIsHydrating(isHydratingDraft);
    }, [isHydratingDraft]);

    useEffect(() => {
        if (!hydrationError) return;
        setError(hydrationError);
    }, [hydrationError]);

    useEffect(() => {
        if (!hydrationData) return;
        setError(null);
        setSuccess(null);
        setCourseTitle(hydrationData.title);
        setDescription(hydrationData.description);
        setPrompts(hydrationData.prompts);
        setDomains(hydrationData.domains.length > 0 ? hydrationData.domains : [""]);
        setFutureGoals(hydrationData.futureGoals.length > 0 ? hydrationData.futureGoals : [""]);
        if (hydrationData.organizationId) {
            setSelectedOrganizationId(hydrationData.organizationId);
        }
        replaceLevels(hydrationData.levels);
    }, [hydrationData, replaceLevels]);

    const updatePrompt = (field: keyof PromptDraft, value: string) => {
        setPrompts((prev) => ({ ...prev, [field]: value }));
    };

    const updateListItem = (
        list: "domains" | "futureGoals",
        index: number,
        value: string,
    ) => {
        if (list === "domains") {
            setDomains((prev) => prev.map((item, idx) => (idx === index ? value : item)));
            return;
        }
        setFutureGoals((prev) => prev.map((item, idx) => (idx === index ? value : item)));
    };

    const addListItem = (list: "domains" | "futureGoals") => {
        if (list === "domains") {
            setDomains((prev) => [...prev, ""]);
            return;
        }
        setFutureGoals((prev) => [...prev, ""]);
    };

    const removeListItem = (list: "domains" | "futureGoals", index: number) => {
        if (list === "domains") {
            setDomains((prev) => prev.filter((_, idx) => idx !== index));
            return;
        }
        setFutureGoals((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleSave = async () => {
        setError(null);
        setSuccess(null);
        if (isSaveDisabled) {
            setError("Please complete all required fields, add content for every lesson, and ensure lesson JSON is valid.");
            return;
        }

        setSaveStatusText("Preparing canonical draft payload...");
        setSaveProgressLog([{ stage: "running", message: "Preparing canonical draft payload..." }]);
        setIsSaving(true);
        const progressId = createClientId();
        const eventSource = new EventSource(
            `/api/admin/canonical-drafts?progress_id=${encodeURIComponent(progressId)}`,
        );
        setShowPostSaveReview(false);
        eventSource.onmessage = (event) => {
            try {
                const snapshot = JSON.parse(event.data) as { stage: string; message: string };
                if (snapshot?.message) {
                    setSaveStatusText(snapshot.message);
                    setSaveProgressLog((prev) => {
                        const last = prev[prev.length - 1];
                        if (last && last.message === snapshot.message && last.stage === snapshot.stage) {
                            return prev;
                        }
                        return [...prev, { stage: snapshot.stage, message: snapshot.message }];
                    });
                }
                if (snapshot?.stage === "completed" || snapshot?.stage === "failed") {
                    eventSource.close();
                }
            } catch {
                // Ignore malformed stream event
            }
        };
        let skipPostSaveLogReview = false;
        try {
            const payload = {
                progress_id: progressId,
                ...(editingCourseId ? { course_id: editingCourseId } : {}),
                title: courseTitle.trim(),
                description: description.trim(),
                organization_id: Number(selectedOrganizationId),
                prompts: {
                    generation_prompt: prompts.generation_prompt.trim(),
                    proactive_prompt: prompts.proactive_prompt.trim(),
                    quiz_prompt: prompts.quiz_prompt.trim(),
                    placement_quiz_prompt: prompts.placement_quiz_prompt.trim(),
                    remedial_lesson_prompt: prompts.remedial_lesson_prompt.trim(),
                },
                metadata: {
                    domains: normalizedDomains,
                    futureGoals: normalizedFutureGoals,
                },
                levels: levels.map((level) => ({
                    ...(level.sourceLevelId ? { level_id: level.sourceLevelId } : {}),
                    name: level.name.trim(),
                    modules: level.modules.map((module) => ({
                        ...(module.sourceModuleId ? { module_id: module.sourceModuleId } : {}),
                        title: module.title.trim(),
                        lessons: module.lessons.map((lesson) => ({
                            ...(lesson.sourceLessonId ? { lesson_id: lesson.sourceLessonId } : {}),
                            title: lesson.title.trim(),
                            content: lesson.content.trim(),
                        })),
                    })),
                })),
            };

            setSaveStatusText(editingCourseId ? "Upserting canonical draft changes..." : "Saving canonical draft...");
            const res = await apiFetch("admin/canonical-drafts", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = (await res.json().catch(() => ({}))) as {
                error?: string;
                courseUuid?: string;
                operations?: SaveOperationGroups;
            };
            if (!res.ok) {
                throw new Error(data.error || "Failed to save canonical draft");
            }
            const operationSummaryText = formatOperationSummary(data.operations);

            setSuccess(
                `${editingCourseId ? "Canonical draft updated." : "Canonical draft saved."}${
                    operationSummaryText ? ` ${operationSummaryText}` : ""
                }`,
            );
            if (data.courseUuid) {
                skipPostSaveLogReview = true;
                setSaveStatusText("Save completed. Redirecting...");
                navigate(`/admin/courses/${data.courseUuid}`);
                return;
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to save canonical draft");
        } finally {
            eventSource.close();
            setIsSaving(false);
            if (skipPostSaveLogReview) {
                dismissSaveReview();
            } else {
                setSaveStatusText(null);
                setShowPostSaveReview(true);
            }
        }
    };

    return (
        <div className="space-y-6 pb-8">
            <header className="sticky top-0 z-20 -mx-4 border-b border-slate-200 bg-white px-4 py-4 shadow-sm sm:-mx-6 sm:px-6 md:-mx-8 md:px-8">
                <div className="flex items-center gap-2">
                    {isEditMode && editingCourseId ? (
                        <button
                            type="button"
                            onClick={() => navigate(`/admin/courses/${editingCourseId}`)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                            aria-label="Back to course"
                            title="Back to course"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                    ) : null}
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                        {isEditMode ? "Edit Canonical Lessons" : "Upload Canonical Lessons"}
                    </h1>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                    {isEditMode
                        ? "Update your existing canonical draft with new or modified levels, modules, and lessons."
                        : "Build canonical lessons with levels, modules, and lessons. Preview the structure on the right."}
                </p>
            </header>

            <div className="flex w-full items-center gap-1 overflow-x-auto rounded-lg border border-slate-200 bg-white p-1">
                <button
                    type="button"
                    onClick={() => setActiveTab("details")}
                    className={`flex-none rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                        activeTab === "details"
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    Course details
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab("metadata")}
                    className={`flex-none rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                        activeTab === "metadata"
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    Course metadata
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab("prompts")}
                    className={`flex-none rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                        activeTab === "prompts"
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    Prompts
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab("structure")}
                    className={`flex-none rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                        activeTab === "structure"
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    Canonical lesson structure
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab("create")}
                    className={`flex-none rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                        activeTab === "create"
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    Create canonical lesson
                </button>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <section className="space-y-5">
                    {activeTab === "details" ? (
                        <CourseBasicsSection
                            courseTitle={courseTitle}
                            description={description}
                            organizations={organizations}
                            selectedOrganizationId={selectedOrganizationId}
                            onCourseTitleChange={setCourseTitle}
                            onDescriptionChange={setDescription}
                            onOrganizationChange={setSelectedOrganizationId}
                        />
                    ) : null}
                    {activeTab === "metadata" ? (
                        <MetadataSection
                            domains={domains}
                            futureGoals={futureGoals}
                            updateListItem={updateListItem}
                            addListItem={addListItem}
                            removeListItem={removeListItem}
                        />
                    ) : null}
                    {activeTab === "prompts" ? (
                        <PromptsSection prompts={prompts} onPromptChange={updatePrompt} />
                    ) : null}
                    {activeTab === "structure" ? (
                        <LevelsEditor
                            levels={levels}
                            addLevel={addLevel}
                            updateLevelName={updateLevelName}
                            removeLevel={removeLevel}
                            addModule={addModule}
                            updateModuleTitle={updateModuleTitle}
                            removeModule={removeModule}
                            addLesson={addLesson}
                            updateLessonField={updateLessonField}
                            removeLesson={removeLesson}
                        />
                    ) : null}
                    {activeTab === "create" ? (
                        <LessonJsonBuilder />
                    ) : null}
                </section>
                <PreviewPanel
                    courseTitle={courseTitle}
                    levels={levels}
                    totalModules={totalModules}
                    totalLessons={totalLessons}
                    error={error}
                    success={success}
                    isSaveDisabled={isSaveDisabled}
                    isSaving={isSaving}
                    onSave={handleSave}
                />
            </div>
            <SaveProgressOverlay
                isSaving={isSaving}
                showPostSaveReview={showPostSaveReview}
                statusText={saveStatusText}
                entries={saveProgressLog}
                onDismissReview={dismissSaveReview}
            />
        </div>
    );
}
